import { GENDER_OPTIONS } from '@kit/shared/gender';

import type {
  ApplicationFieldConfig,
  ApplicationStructure,
} from '../types/audition.types';

/**
 * Competition-enforced application fields — the data the scholarship PDF
 * booklet (US-112) needs. These are injected as `locked` fields into a
 * competition's application structure: the organizer can't remove them and
 * can't unset their `required` flag, but may freely add other fields and
 * arrange steps. Mirrors Meteor's fixed SCHOOL/COMPANY structures via an
 * explicit lock (parity map §3/§11).
 *
 * Stable keys are reused verbatim from the Meteor structures so the booklet
 * (and any Meteor-imported application data) resolves them consistently.
 *
 * Profile-bound fields (`source: 'profile'`) prefill/backfill via
 * PROFILE_FIELD_MAP. The three contact/school fields are application-private
 * (no profile column) — they live only on the application.
 */
export const COMPETITION_BOOKLET_FIELDS: Readonly<
  Omit<ApplicationFieldConfig, 'sortOrder'>[]
> = [
  {
    key: 'firstname',
    type: 'text',
    label: 'First Name',
    source: 'profile',
    locked: true,
    validation: { required: true, maxLength: 100 },
    width: 'half',
  },
  {
    key: 'lastname',
    type: 'text',
    label: 'Last Name',
    source: 'profile',
    locked: true,
    validation: { required: true, maxLength: 100 },
    width: 'half',
  },
  {
    key: 'birthdate',
    type: 'date',
    label: 'Date of Birth',
    source: 'profile',
    locked: true,
    validation: { required: true },
    width: 'half',
  },
  {
    key: 'gender',
    type: 'radio',
    label: 'Gender',
    source: 'profile',
    locked: true,
    // Full gender set from the canonical registry — the booklet field must be
    // able to label existing `nonBinary` data (the apply input offers only the
    // binary subset; see competition-application-structure.ts).
    options: GENDER_OPTIONS,
    width: 'half',
  },
  {
    key: 'residenceCountry',
    type: 'select',
    label: 'Country of Residence',
    source: 'profile',
    locked: true,
    widget: 'country',
    width: 'half',
  },
  {
    // BAL-372: height is mandatory on competition applications. The canonical
    // default (competition-application-structure.ts) already marks it required,
    // but already-seeded structures (e.g. gdo-finals-2026) stored height as
    // optional — listing it here makes `ensureEnforcedFields` re-apply
    // `required` at read-time so the live form enforces it without a data
    // migration. Profile-bound (`height_cm`) so it prefills/backfills.
    key: 'height',
    type: 'number',
    label: 'Height (cm)',
    source: 'profile',
    locked: true,
    validation: { required: true, min: 60, max: 250 },
    width: 'half',
  },
  {
    key: 'contactEmail',
    type: 'email',
    label: 'Contact Email',
    source: 'profile',
    locked: true,
    validation: { required: true },
    width: 'full',
  },
  // Application-private booklet fields (no profile column).
  {
    key: 'parentContactEmail',
    type: 'email',
    label: 'Parent / Guardian Email',
    description: 'For applicants under 18.',
    locked: true,
    width: 'half',
  },
  {
    key: 'teacherContactEmail',
    type: 'email',
    label: 'Teacher / School Email',
    locked: true,
    width: 'half',
  },
  {
    key: 'currentBalletSchool',
    type: 'text',
    label: 'Current Ballet School',
    locked: true,
    width: 'half',
  },
];

/**
 * The form key holding the date of birth — the anchor the `age_category`
 * field is inserted immediately after (so the dancer's category appears as a
 * direct consequence of the DOB they just entered). Matches the canonical
 * structure's `birthdate` field (sortOrder 3).
 */
const DOB_FIELD_KEY = 'birthdate';

/**
 * The Category step is anchored AFTER the profile inputs, so the dancer fills
 * their personal details (gender / height / nationalities / residence) on the
 * Profile step before the category selection appears as its own step. Falls
 * back to the DOB anchor when the residence field is absent.
 */
const STYLES_ANCHOR_KEY = 'residenceCountry';

/**
 * The heading field that declares the dedicated Category step. The canonical
 * structure bakes this heading in (competition-application-structure.ts); stored
 * / legacy structures that predate it (e.g. gdo-finals-2026) don't carry it, so
 * `insertPositionalFields` / the group path synthesize it at read-time right
 * after the profile block. Keeping the positional fields under their own heading
 * is what makes category selection a separate step from Profile.
 */
const CATEGORY_SECTION_KEY = 'categorySection';
const CATEGORY_SECTION_LABEL = 'Category';

/**
 * Positionally-injected competition fields, distinct from the appended booklet
 * fields above. Both land in a dedicated Category step (declared by the
 * `categorySection` heading) placed right after the profile block — so the
 * Profile step stays profile-only and category selection is its own step. This
 * must hold even on existing competitions whose stored structure predates the
 * heading, so they can't go through the generic "append missing to the end"
 * path. They carry no `source` (neither is profile-bound: age is derived, styles
 * persist to the style-entries join).
 */
export const COMPETITION_POSITIONAL_FIELDS: Readonly<
  Omit<ApplicationFieldConfig, 'sortOrder'>[]
> = [
  {
    key: 'age_category',
    type: 'age_category',
    label: 'Your Category',
    description:
      'Auto-assigned from your date of birth. You cannot change this.',
    locked: true,
    width: 'half',
  },
  {
    key: 'competition_styles',
    type: 'competition_styles',
    label: 'Your Styles',
    description: 'Select every style you compete in.',
    locked: true,
    validation: { required: true },
    width: 'full',
  },
];

// `hasAllEnforcedFields` answers "does this structure carry every enforced
// BOOKLET field?" (the PDF-booklet contract). The positional fields
// (age_category, competition_styles) are deliberately NOT part of this set:
// they are injected at read-time by `ensureEnforcedFields`, not baked into the
// canonical structure, so a canonical structure legitimately lacks them.
const ENFORCED_BOOKLET_KEYS = new Set(
  COMPETITION_BOOKLET_FIELDS.map((f) => f.key),
);

// `upgrade` re-applies locked/required to ANY enforced field it finds in a
// structure — both booklet and positional — so this map spans both sets.
const BY_KEY = new Map(
  [...COMPETITION_BOOKLET_FIELDS, ...COMPETITION_POSITIONAL_FIELDS].map(
    (f) => [f.key, f] as const,
  ),
);

/** Every field key in the structure, across both the step model and flat list. */
function allKeys(structure: ApplicationStructure): Set<string> {
  const keys = new Set<string>();
  for (const f of structure.fields ?? []) keys.add(f.key);
  for (const g of structure.groups ?? []) {
    for (const f of g.fields) keys.add(f.key);
  }
  return keys;
}

/** True when every enforced booklet field is present in the structure. */
export function hasAllEnforcedFields(structure: ApplicationStructure): boolean {
  const present = allKeys(structure);
  for (const key of ENFORCED_BOOKLET_KEYS) {
    if (!present.has(key)) return false;
  }
  return true;
}

/** Re-apply locked + required to a field when it is an enforced field. */
function upgrade(field: ApplicationFieldConfig): ApplicationFieldConfig {
  const canonical = BY_KEY.get(field.key);
  if (!canonical) return field;
  return {
    ...field,
    locked: true,
    validation: canonical.validation?.required
      ? { ...field.validation, required: true }
      : field.validation,
  };
}

/**
 * Insert the positional fields into a flat field list as a dedicated Category
 * step: they land directly after the `categorySection` heading so they form
 * their own step rather than extending the Profile step. When the structure
 * predates that heading (stored/legacy competitions), the heading is synthesized
 * right after the profile block — anchored after `residenceCountry`
 * (STYLES_ANCHOR_KEY), then the DOB field, then appended — so a field is never
 * dropped. Only inserts the ones not already present. Returns a re-sequenced
 * copy; never mutates input. The list is expected to be already `upgrade`d.
 */
function insertPositionalFields(
  fields: ApplicationFieldConfig[],
): ApplicationFieldConfig[] {
  const missing = COMPETITION_POSITIONAL_FIELDS.filter(
    (pf) => !fields.some((f) => f.key === pf.key),
  );
  // Nothing to inject — return a re-sequenced copy without touching the heading
  // (so an already-injected structure stays byte-stable on a second pass).
  if (missing.length === 0) {
    return fields.map((f, i) => ({ ...f, sortOrder: i }));
  }

  const result = [...fields];

  // Ensure the Category-step heading exists. Synthesize it after the profile
  // block when a stored/legacy structure predates it.
  let headingIdx = result.findIndex((f) => f.key === CATEGORY_SECTION_KEY);
  if (headingIdx < 0) {
    let anchorIdx = -1;
    for (const anchor of [STYLES_ANCHOR_KEY, DOB_FIELD_KEY]) {
      anchorIdx = result.findIndex((f) => f.key === anchor);
      if (anchorIdx >= 0) break;
    }
    const heading: ApplicationFieldConfig = {
      key: CATEGORY_SECTION_KEY,
      type: 'heading',
      label: CATEGORY_SECTION_LABEL,
      sortOrder: 0,
    };
    if (anchorIdx >= 0) {
      result.splice(anchorIdx + 1, 0, heading);
      headingIdx = anchorIdx + 1;
    } else {
      result.push(heading);
      headingIdx = result.length - 1;
    }
  }

  // Insert the missing positional fields directly after the heading, preserving
  // declaration order (age_category before competition_styles).
  let insertAt = headingIdx + 1;
  for (const field of missing) {
    result.splice(insertAt, 0, { ...field, sortOrder: 0 });
    insertAt += 1;
  }

  // Re-sequence sortOrder across the whole list so the inserted fields slot in
  // positionally rather than carrying a stale/absent order.
  return result.map((f, i) => ({ ...f, sortOrder: i }));
}

/**
 * Ensure a competition structure contains all enforced fields, locked and
 * required where the canonical definition says so. Two classes of enforced
 * field are handled:
 *
 * - **Booklet fields** (`COMPETITION_BOOKLET_FIELDS`) — appended to the end
 *   when missing (their exact position doesn't matter for the booklet PDF).
 * - **Positional fields** (`COMPETITION_POSITIONAL_FIELDS`: age_category,
 *   competition_styles) — placed in a dedicated Category step right after the
 *   profile block, so the Profile step stays profile-only and category
 *   selection is its own step. On the step model this is a new group inserted
 *   after the group holding the DOB field (unless a Category group already
 *   exists); on the flat model it's the `categorySection` heading. This must
 *   hold even on existing competitions, so they can't use the generic append
 *   path.
 *
 * Existing fields with an enforced key are upgraded in place (locked + required
 * re-applied) without disturbing their position. Operates on the step model
 * (`groups`) when present — the stepper renders from groups — otherwise on the
 * flat `fields`. Never mutates input; idempotent.
 */
export function ensureEnforcedFields(
  structure: ApplicationStructure,
): ApplicationStructure {
  const presentKeys = allKeys(structure);
  const missingBooklet = COMPETITION_BOOKLET_FIELDS.filter(
    (f) => !presentKeys.has(f.key),
  );

  // Step-model structures: upgrade across all groups, insert positional fields
  // into whichever group carries the DOB field (else the first group), and
  // append missing booklet fields to the last group.
  if (structure.groups && structure.groups.length > 0) {
    let groups = structure.groups.map((g) => ({
      ...g,
      fields: g.fields.map(upgrade),
    }));

    // Positional insertion: category fields live in their own step. Reuse an
    // existing Category group when present; otherwise create a new one right
    // after the group holding the DOB field (the Profile step).
    const needsPositional = COMPETITION_POSITIONAL_FIELDS.some(
      (f) => !presentKeys.has(f.key),
    );
    if (needsPositional) {
      const categoryGroupIndex = groups.findIndex(
        (g) =>
          g.key === CATEGORY_SECTION_KEY ||
          g.fields.some(
            (f) => f.type === 'age_category' || f.type === 'competition_styles',
          ),
      );
      if (categoryGroupIndex >= 0) {
        // Append the missing positional fields to the existing Category group.
        groups = groups.map((g, i) => {
          if (i !== categoryGroupIndex) return g;
          let order = g.fields.reduce(
            (max, f) => Math.max(max, f.sortOrder ?? 0),
            -1,
          );
          const additions = COMPETITION_POSITIONAL_FIELDS.filter(
            (pf) => !g.fields.some((f) => f.key === pf.key),
          ).map((pf) => ({ ...pf, sortOrder: ++order }));
          return { ...g, fields: [...g.fields, ...additions] };
        });
      } else {
        const dobGroupIndex = groups.findIndex((g) =>
          g.fields.some((f) => f.key === DOB_FIELD_KEY),
        );
        const insertAfter = dobGroupIndex >= 0 ? dobGroupIndex : 0;
        const categoryGroup = {
          key: CATEGORY_SECTION_KEY,
          name: CATEGORY_SECTION_LABEL,
          fields: COMPETITION_POSITIONAL_FIELDS.map((f, i) => ({
            ...f,
            sortOrder: i,
          })),
          sortOrder: 0,
        };
        groups = [
          ...groups.slice(0, insertAfter + 1),
          categoryGroup,
          ...groups.slice(insertAfter + 1),
        ].map((g, i) => ({ ...g, sortOrder: i }));
      }
    }

    if (missingBooklet.length > 0) {
      const last = groups[groups.length - 1]!;
      let order = last.fields.reduce(
        (max, f) => Math.max(max, f.sortOrder ?? 0),
        0,
      );
      last.fields = [
        ...last.fields,
        ...missingBooklet.map((f) => ({ ...f, sortOrder: ++order })),
      ];
    }
    return { ...structure, groups };
  }

  // Flat structures: upgrade in place, insert positional fields after DOB,
  // append missing booklet fields at the end.
  const upgraded = (structure.fields ?? []).map(upgrade);
  const withPositional = insertPositionalFields(upgraded);
  let order = withPositional.reduce(
    (max, f) => Math.max(max, f.sortOrder ?? 0),
    0,
  );
  const appended = missingBooklet.map((f) => ({ ...f, sortOrder: ++order }));
  return { ...structure, fields: [...withPositional, ...appended] };
}
